import { CanvasTexture, NearestFilter, Sprite, SpriteMaterial } from "three";

export interface NameTagOptions {
	font?: string;
	repaintAfterLoaded?: boolean;
	margin?: [number, number, number, number];
	textStyle?: string | CanvasGradient | CanvasPattern;
	backgroundStyle?: string | CanvasGradient | CanvasPattern;
	height?: number;

	dividerStyle?: string | CanvasGradient | CanvasPattern;
	isPremiumRank?: boolean;
	rank?: string;
	isBold?: boolean;
}

export class NameTagObject extends Sprite {
	readonly painted: Promise<void>;

	private text: string;
	private font: string;
	private margin: [number, number, number, number];
	private textStyle: string | CanvasGradient | CanvasPattern;
	private dividerStyle: string | CanvasGradient | CanvasPattern;
	private backgroundStyle: string | CanvasGradient | CanvasPattern;
	private height: number;
	private textMaterial: SpriteMaterial;
	private isPremiumRank: boolean;
	private isBold: boolean;
	private divider: string;

	constructor(text: string = "", options: NameTagOptions = {}) {
		const material = new SpriteMaterial({
			transparent: true,
			alphaTest: 1e-5,
		});

		super(material);

		this.textMaterial = material;
		this.isPremiumRank = options.isPremiumRank === undefined ? false : options.isPremiumRank;
		this.isBold = options.isBold === undefined ? false : options.isBold;

		this.text = options.isPremiumRank ? `${options.rank} | ${text}` : text;
		this.font = options.font === undefined ? "20px Mojangles" : options.font;
		this.margin = options.margin === undefined ? [5, 2, 5, 2] : options.margin;
		this.textStyle = options.textStyle === undefined ? "white" : options.textStyle;
		this.dividerStyle = options.dividerStyle === undefined ? "#555555" : options.dividerStyle;
		this.backgroundStyle = options.backgroundStyle === undefined ? "rgba(0,0,0,.15)" : options.backgroundStyle;
		this.height = options.height === undefined ? 4.0 : options.height;
		this.divider = " | ";

		const repaintAfterLoaded = options.repaintAfterLoaded === undefined ? true : options.repaintAfterLoaded;
		if (repaintAfterLoaded && !document.fonts.check(this.font, this.text)) {
			this.paint();
			this.painted = this.loadAndPaint();
		} else {
			this.paint();
			this.painted = Promise.resolve();
		}
	}

	private async loadAndPaint() {
		await document.fonts.load(this.font, this.text);
		this.paint();
	}

	private paint() {
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d")!;

		// Set the font style for the rank if isBold is true
		const rankFont = this.isBold ? `bold ${this.font}` : this.font;
		const textFont = this.font;

		ctx.font = rankFont;

		const beforeMetrics = ctx.measureText(this.isPremiumRank ? this.text.split(this.divider)[0] : this.text);
		const afterMetrics = this.isPremiumRank ? ctx.measureText(this.text.split(this.divider)[1]) : undefined;
		const dividerMetrics = this.isPremiumRank ? ctx.measureText(this.divider) : undefined;

		canvas.width =
			this.margin[3] +
			beforeMetrics.width +
			(this.isPremiumRank ? this.margin[1] + dividerMetrics!.width + this.margin[1] : 0) +
			(this.isPremiumRank ? afterMetrics!.width : 0) +
			this.margin[1];
		canvas.height =
			this.margin[0] + beforeMetrics.actualBoundingBoxAscent + beforeMetrics.actualBoundingBoxDescent + this.margin[2];

		// Draw background
		ctx.fillStyle = this.backgroundStyle;
		ctx.fillRect(0, 0, canvas.width, canvas.height);

		// Draw the rank text (if applicable) with the bold font
		if (this.isPremiumRank) {
			ctx.font = rankFont;
			ctx.fillStyle = this.textStyle;
			ctx.fillText(
				this.text.split(this.divider)[0],
				this.margin[3],
				this.margin[0] + beforeMetrics.actualBoundingBoxAscent
			);

			const dividerXPos = this.margin[3] + beforeMetrics.width + this.margin[1];

			// Draw the divider
			ctx.fillStyle = this.dividerStyle;
			ctx.fillText(this.divider, dividerXPos, this.margin[0] + dividerMetrics!.actualBoundingBoxAscent);

			// Draw the remaining text with the regular font
			ctx.font = textFont;
			ctx.fillStyle = this.textStyle;
			ctx.fillText(
				this.text.split(this.divider)[1],
				dividerXPos + dividerMetrics!.width + this.margin[1],
				this.margin[0] + afterMetrics!.actualBoundingBoxAscent
			);
		} else {
			// Draw the regular text if no premium rank is set
			ctx.font = textFont;
			ctx.fillStyle = this.textStyle;
			ctx.fillText(this.text, this.margin[3], this.margin[0] + beforeMetrics.actualBoundingBoxAscent);
		}

		const texture = new CanvasTexture(canvas);
		texture.magFilter = NearestFilter;
		texture.minFilter = NearestFilter;
		this.textMaterial.map = texture;
		this.textMaterial.needsUpdate = true;

		this.scale.x = (canvas.width / canvas.height) * this.height;
		this.scale.y = this.height;
	}
}
